import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title } from "chart.js";
import { JSP } from "../theme/jspColors";

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

function parts(assigned, available, retired, total) {
  const a = Math.max(0, Number(assigned) || 0);
  const v = Math.max(0, Number(available) || 0);
  const r = Math.max(0, Number(retired) || 0);
  const t = Math.max(Number(total) || 0, a + v + r);
  return { a, v, r, t };
}

export default function LicenseUsageBar({
  assigned = 0,
  available = 0,
  retired = 0,
  total = 0,
  compact = false,
}) {
  const { a, v, r, t } = parts(assigned, available, retired, total);
  const assignedPct = t ? (a / t) * 100 : 0;
  const availablePct = t ? (v / t) * 100 : 0;
  const retiredPct = t ? (r / t) * 100 : 0;

  const chartData = useMemo(() => {
    const datasets = [
      a > 0 && {
        label: "Zugewiesen",
        data: [a],
        backgroundColor: JSP.blue,
        borderSkipped: false,
      },
      v > 0 && {
        label: "Verfügbar",
        data: [v],
        backgroundColor: JSP.green,
        borderSkipped: false,
      },
      r > 0 && {
        label: "Zurückgezogen",
        data: [r],
        backgroundColor: JSP.ink,
        borderSkipped: false,
      },
    ].filter(Boolean);
    if (!datasets.length) {
      datasets.push({
        label: "Gesamt",
        data: [t],
        backgroundColor: JSP.mist,
        borderSkipped: false,
      });
    }
    return { labels: ["Lizenzen"], datasets };
  }, [a, v, r, t]);

  const chartOptions = useMemo(() => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Lizenznutzung",
        align: "start",
      },
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: t || undefined,
        title: { display: true, text: "Anzahl Lizenzen" },
        ticks: { precision: 0 },
      },
      y: {
        stacked: true,
        display: false,
      },
    },
  }), [t]);

  if (compact) {
    return (
      <div style={{ minWidth: 120 }}>
        <div className="progress" style={{ height: 10 }} title={`Zugewiesen ${a} · Verfügbar ${v} · Gesamt ${t}`}>
          <div className="progress-bar bg-primary" style={{ width: `${assignedPct}%` }} />
          <div className="progress-bar bg-success" style={{ width: `${availablePct}%` }} />
          <div className="progress-bar bg-secondary" style={{ width: `${retiredPct}%` }} />
        </div>
        <div className="small text-muted mt-1">{v} / {t} frei</div>
      </div>
    );
  }

  if (!t) {
    return <p className="text-muted mb-0">Keine Lizenzdaten vorhanden.</p>;
  }

  return (
    <div>
      <div style={{ height: 140 }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <div className="d-flex flex-wrap gap-3 small mt-2">
        <span><strong>Zugewiesen:</strong> {a}</span>
        <span><strong>Verfügbar:</strong> {v}</span>
        {r > 0 && <span><strong>Zurückgezogen:</strong> {r}</span>}
        <span><strong>Gesamt:</strong> {t}</span>
      </div>
    </div>
  );
}
